import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  InputNumber,
  Input,
  Modal,
  Progress,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, InboxOutlined, SendOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useMutation, useQuery } from '@apollo/client';
import {
  SEND_SMS_BY_PHONE,
  SendSmsByPhoneResult,
} from './graphql/mutations/sendSmsByPhone';
import {
  SMS_TOOL_BATCH_MAX_ROWS,
  SmsToolBatchMaxRowsResult,
} from './graphql/queries/smsToolBatchMaxRows';
import { parseSmsExcel, SmsRow } from './utils/parseExcel';

// 单次导入行数上限兜底值：后端配置中心读取失败时使用，避免一次群发过多（研发工具，顺序发送）
const DEFAULT_MAX_ROWS = 200;

type SendState = 'pending' | 'sending' | 'success' | 'failed';

interface RowStatus {
  state: SendState;
  reason?: string;
}

const SmsToolPage: React.FC = () => {
  const [rows, setRows] = useState<SmsRow[]>([]);
  const [fileName, setFileName] = useState('');
  // 行无区域列/为空时回退的默认国家区号
  const [defaultCountryCode, setDefaultCountryCode] = useState<number>(1);
  // 行无内容列/为空时回退的统一短信内容
  const [globalMessage, setGlobalMessage] = useState('');
  const [statusMap, setStatusMap] = useState<Record<number, RowStatus>>({});
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // 单个发送
  const [singleCountryCode, setSingleCountryCode] = useState<number>(1);
  const [singlePhone, setSinglePhone] = useState('');
  const [singleMessage, setSingleMessage] = useState('');
  const [singleSending, setSingleSending] = useState(false);
  const [singleResult, setSingleResult] = useState<string | null>(null);

  const [sendSmsByPhone] = useMutation<{ sendSmsByPhone: SendSmsByPhoneResult }>(SEND_SMS_BY_PHONE);

  // 批量导入行数上限：从配置中心读取（走 Redis 缓存），读取失败回落兜底值，便于在线调整无需发版
  const { data: maxRowsData } = useQuery<SmsToolBatchMaxRowsResult>(SMS_TOOL_BATCH_MAX_ROWS, {
    fetchPolicy: 'cache-and-network',
  });
  const maxRows = maxRowsData?.smsToolBatchMaxRows ?? DEFAULT_MAX_ROWS;

  const doSingleSend = async () => {
    setSingleSending(true);
    setSingleResult(null);
    try {
      const { data } = await sendSmsByPhone({
        variables: {
          input: {
            countryCode: singleCountryCode,
            phone: singlePhone.trim(),
            message: singleMessage,
          },
        },
      });
      const res = data?.sendSmsByPhone;
      if (res?.success) {
        setSingleResult(`发送成功${res.messageId ? `（messageId: ${res.messageId}）` : ''}`);
        message.success('发送成功');
      } else {
        const reason = res?.errorMessage ?? '发送失败';
        setSingleResult(`发送失败：${reason}`);
        message.error(reason);
      }
    } catch (e) {
      const reason =
        (e as { graphQLErrors?: { message: string }[] })?.graphQLErrors?.[0]?.message ??
        (e as Error)?.message ??
        '调用失败';
      setSingleResult(`调用失败：${reason}`);
      message.error('调用失败');
    } finally {
      setSingleSending(false);
    }
  };

  const handleSingleSendClick = () => {
    const phone = singlePhone.trim();
    if (!phone) {
      message.warning('请输入手机号');
      return;
    }
    if (!/^\d+$/.test(phone)) {
      message.warning('手机号只能包含数字（不含区号/+）');
      return;
    }
    if (!singleMessage.trim()) {
      message.warning('请输入短信内容');
      return;
    }
    Modal.confirm({
      title: '确认发送短信',
      content: `将向 +${singleCountryCode} ${phone} 发送短信，发送后无法撤回且可能产生运营商费用。确认继续？`,
      okText: '确认发送',
      cancelText: '取消',
      onOk: doSingleSend,
    });
  };

  // 行的最终国家区号：区域列缺失/为空时用默认值
  const effectiveCountryCode = (row: SmsRow): number | null =>
    row.regionRaw ? row.countryCode : defaultCountryCode;

  // 行的最终内容：行内无内容时回退统一内容
  const effectiveMessage = (row: SmsRow): string => row.message || globalMessage;

  // 行是否可发送（校验通过 + 有区号 + 有内容）
  const rowReady = (row: SmsRow): boolean =>
    !row.error && effectiveCountryCode(row) !== null && !!effectiveMessage(row);

  const readyCount = useMemo(() => rows.filter(rowReady).length, [rows, defaultCountryCode, globalMessage]);

  // 下载导入模板：表头与 parseExcel 的列名匹配规则一致，并给两行示例
  const handleDownloadTemplate = () => {
    const data = [
      ['手机号', '区域', '内容'],
      ['2345678912', '1', 'Hello from SwiftX'],
      ['18618256606', '86', '你好，这是一条测试短信'],
    ];
    const sheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    XLSX.writeFile(workbook, '短信发送模板.xlsx');
  };

  const handleFile = async (file: File) => {
    try {
      const result = await parseSmsExcel(file);
      if (result.fatalError) {
        message.error(result.fatalError);
        return;
      }
      if (result.rows.length > maxRows) {
        message.error(`单次最多导入 ${maxRows} 行，当前 ${result.rows.length} 行，请拆分后重试`);
        return;
      }
      setRows(result.rows);
      setFileName(file.name);
      setStatusMap({});
      setProgress({ done: 0, total: 0 });
      if (!result.hasRegionColumn) {
        message.info('未识别到区域列，将对所有行使用「默认国家区号」');
      }
      if (!result.hasMessageColumn) {
        message.info('未识别到内容列，将对所有行使用「统一短信内容」');
      }
      const invalid = result.rows.filter((r) => r.error).length;
      message.success(`解析成功：${result.rows.length} 行${invalid ? `，其中 ${invalid} 行有问题` : ''}`);
    } catch (e) {
      message.error(`解析失败：${(e as Error).message}`);
    }
  };

  const doSend = async () => {
    setSending(true);
    const targets = rows.filter(rowReady);
    setProgress({ done: 0, total: targets.length });
    let done = 0;
    for (const row of targets) {
      setStatusMap((prev) => ({ ...prev, [row.rowNo]: { state: 'sending' } }));
      try {
        const { data } = await sendSmsByPhone({
          variables: {
            input: {
              countryCode: effectiveCountryCode(row) as number,
              phone: row.phone,
              message: effectiveMessage(row),
            },
          },
        });
        const res = data?.sendSmsByPhone;
        if (res?.success) {
          setStatusMap((prev) => ({ ...prev, [row.rowNo]: { state: 'success' } }));
        } else {
          setStatusMap((prev) => ({
            ...prev,
            [row.rowNo]: { state: 'failed', reason: res?.errorMessage ?? '发送失败' },
          }));
        }
      } catch (e) {
        const reason =
          (e as { graphQLErrors?: { message: string }[] })?.graphQLErrors?.[0]?.message ??
          (e as Error)?.message ??
          '调用失败';
        setStatusMap((prev) => ({ ...prev, [row.rowNo]: { state: 'failed', reason } }));
      }
      done += 1;
      setProgress({ done, total: targets.length });
    }
    setSending(false);
    message.success('发送流程结束，详见下方状态列');
  };

  const handleSendClick = () => {
    if (readyCount === 0) {
      message.warning('没有可发送的行，请检查手机号、区域、内容');
      return;
    }
    Modal.confirm({
      title: '确认发送短信',
      content: `将向 ${readyCount} 个手机号发送短信，发送后无法撤回且可能产生运营商费用。确认继续？`,
      okText: '确认发送',
      cancelText: '取消',
      onOk: doSend,
    });
  };

  const renderStatus = (row: SmsRow) => {
    const st = statusMap[row.rowNo];
    if (st?.state === 'sending') return <Tag color="processing">发送中</Tag>;
    if (st?.state === 'success') return <Tag color="success">已发送</Tag>;
    if (st?.state === 'failed') {
      return (
        <Tag color="error" title={st.reason}>
          失败：{st.reason}
        </Tag>
      );
    }
    if (row.error) return <Tag color="warning">{row.error}</Tag>;
    if (!rowReady(row)) {
      if (effectiveCountryCode(row) === null) return <Tag color="warning">缺国家区号</Tag>;
      if (!effectiveMessage(row)) return <Tag color="warning">缺短信内容</Tag>;
    }
    return <Tag>待发送</Tag>;
  };

  const columns: ColumnsType<SmsRow> = [
    { title: '行号', dataIndex: 'rowNo', width: 70 },
    { title: '手机号', dataIndex: 'phone', width: 160 },
    {
      title: '区域',
      width: 120,
      render: (_, row) => row.regionRaw || <span className="text-gray-400">（默认）</span>,
    },
    {
      title: '国家区号',
      width: 100,
      render: (_, row) => effectiveCountryCode(row) ?? <span className="text-red-400">?</span>,
    },
    {
      title: '短信内容',
      render: (_, row) =>
        effectiveMessage(row) || <span className="text-red-400">（空）</span>,
    },
    { title: '状态', width: 220, render: (_, row) => renderStatus(row) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Typography.Title level={4}>短信工具</Typography.Title>

      <Alert
        type="warning"
        showIcon
        message="研发工具：导入 Excel 批量发送短信。任意环境都会真正发送、产生运营商实费且无法撤回。请谨慎使用。"
      />

      <Tabs
        defaultActiveKey="single"
        items={[
          {
            key: 'single',
            label: '单个发送',
            children: (
              <Card size="small">
                <Space direction="vertical" className="w-full" size="middle">
                  <Space wrap>
                    <span>
                      国家区号：
                      <InputNumber
                        min={1}
                        value={singleCountryCode}
                        onChange={(v) => setSingleCountryCode(Number(v) || 1)}
                        style={{ width: 120 }}
                      />
                    </span>
                    <span>
                      手机号：
                      <Input
                        value={singlePhone}
                        onChange={(e) => setSinglePhone(e.target.value)}
                        placeholder="不含区号/+ 的本地裸号"
                        style={{ width: 240 }}
                      />
                    </span>
                  </Space>
                  <div>
                    <Typography.Text>短信内容：</Typography.Text>
                    <Input.TextArea
                      value={singleMessage}
                      onChange={(e) => setSingleMessage(e.target.value)}
                      rows={3}
                      placeholder="短信内容"
                    />
                  </div>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={singleSending}
                    onClick={handleSingleSendClick}
                  >
                    发送
                  </Button>
                  {singleResult && (
                    <Alert
                      type={singleResult.startsWith('发送成功') ? 'success' : 'error'}
                      message={singleResult}
                    />
                  )}
                </Space>
              </Card>
            ),
          },
          {
            key: 'batch',
            label: '批量导入（Excel）',
            children: (
              <div className="flex flex-col gap-4">
                <Card
                  title="① 导入与默认值"
                  size="small"
                  extra={
                    <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                      下载模板
                    </Button>
                  }
                >
        <Space direction="vertical" className="w-full" size="middle">
          <Upload.Dragger
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              handleFile(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽 Excel 文件到此处</p>
            <p className="ant-upload-hint">
              支持 .xlsx / .xls / .csv；表头需包含「手机号」列，可选「区域」（国家区号，如 1/86/52）与「内容」列。
              若用 Numbers 打开过模板，请先「导出为 Excel(.xlsx)」再上传，不要上传 .numbers 文件。
            </p>
          </Upload.Dragger>
          {fileName && <Typography.Text type="secondary">已导入：{fileName}</Typography.Text>}

          <Space wrap>
            <span>
              默认国家区号：
              <InputNumber
                min={1}
                value={defaultCountryCode}
                onChange={(v) => setDefaultCountryCode(Number(v) || 1)}
                style={{ width: 120 }}
              />
            </span>
          </Space>
          <div>
            <Typography.Text>统一短信内容（行内无内容列时使用）：</Typography.Text>
            <Input.TextArea
              value={globalMessage}
              onChange={(e) => setGlobalMessage(e.target.value)}
              rows={3}
              placeholder="当 Excel 没有内容列或某行内容为空时，使用这里填写的内容"
            />
          </div>
        </Space>
      </Card>

      {rows.length > 0 && (
        <Card
          title={`② 预览与发送（共 ${rows.length} 行，可发送 ${readyCount} 行）`}
          size="small"
          extra={
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={readyCount === 0}
              onClick={handleSendClick}
            >
              发送（{readyCount}）
            </Button>
          }
        >
          <Space direction="vertical" className="w-full">
            {progress.total > 0 && (
              <Progress
                percent={Math.round((progress.done / progress.total) * 100)}
                format={() => `${progress.done}/${progress.total}`}
              />
            )}
            <Table
              rowKey="rowNo"
              size="small"
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
            />
          </Space>
        </Card>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SmsToolPage;
